import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import EkgStrip from "@/components/EkgStrip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CheckCircle2, XCircle, Trophy, RotateCcw, BookOpen, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NinjaBadge from "@/components/NinjaBadge";

export default function Level() {
  const { id } = useParams();
  const levelId = parseInt(id);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [level, setLevel] = useState(null);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("lessons"); // lessons | quiz | result
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setStage("lessons");
    setStep(0);
    setAnswers([]);
    setResult(null);
    api
      .get(`/levels/${levelId}`)
      .then(({ data }) => {
        setLevel(data);
        setAnswers(new Array(data.quiz.length).fill(null));
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, [levelId]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <Navbar />
        <div className="max-w-2xl mx-auto p-12 text-center">
          <div className="font-mono text-xs uppercase tracking-widest text-red-400 mb-3">
            Locked
          </div>
          <h1 className="font-display text-3xl font-bold mb-4">This belt is not unlocked yet</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/dashboard">
            <Button className="rounded-full bg-green-500 hover:bg-green-400 text-black" data-testid="locked-back-btn">
              Back to Dojo
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!level) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <Navbar />
        <div className="p-12 text-slate-500 font-mono text-sm">Loading level…</div>
      </div>
    );
  }

  const totalLessons = level.lessons.length;
  const totalQuestions = level.quiz.length;

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/submit", {
        level_id: levelId,
        answers,
      });
      setResult(data);
      setStage("result");
      await refresh();
      if (data.passed) {
        toast.success(`+${data.xp_earned} XP · ${data.new_rank || "Belt earned"}`);
      } else {
        toast.error(`${data.percent}% — 80% needed to advance`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const retryQuiz = () => {
    setAnswers(new Array(totalQuestions).fill(null));
    setStep(0);
    setResult(null);
    setStage("quiz");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />

      {/* Header */}
      <div className="border-b border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <NinjaBadge level={level.id} size={56} glow />
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-green-400">
                LVL {String(level.id).padStart(2, "0")} · {level.tagline}
              </div>
              <h1 className="font-display text-2xl font-bold mt-1" data-testid="level-title">
                {level.rank}
              </h1>
            </div>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-400 hover:text-white flex items-center gap-1" data-testid="back-to-dashboard">
            <ChevronLeft className="w-4 h-4" /> Dojo
          </Link>
        </div>
      </div>

      {/* Content */}
      {stage === "lessons" && (
        <LessonView
          level={level}
          step={step}
          setStep={setStep}
          onFinish={() => {
            setStage("quiz");
            setStep(0);
          }}
        />
      )}

      {stage === "quiz" && (
        <QuizView
          level={level}
          step={step}
          setStep={setStep}
          answers={answers}
          setAnswers={setAnswers}
          onSubmit={submitQuiz}
          submitting={submitting}
        />
      )}

      {stage === "result" && result && (
        <ResultView
          level={level}
          result={result}
          onRetry={retryQuiz}
          onReviewLessons={() => {
            setStage("lessons");
            setStep(0);
          }}
        />
      )}
    </div>
  );
}

function LessonView({ level, step, setStep, onFinish }) {
  const lesson = level.lessons[step];
  const total = level.lessons.length;
  const progress = Math.round(((step + 1) / total) * 100);
  const [showDeep, setShowDeep] = useState(false);

  // Reset deep-dive collapse when the lesson step changes
  useEffect(() => {
    setShowDeep(false);
  }, [step]);

  const hasDeep = Array.isArray(lesson.deep) && lesson.deep.length > 0;

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10">
      <div className="mb-6">
        <div className="flex justify-between text-xs font-mono text-slate-500 mb-2">
          <span>
            Lesson {step + 1}/{total}
          </span>
          <span>{progress}%</span>
        </div>
        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6" data-testid={`lesson-${step}`}>
        <div className="lg:col-span-3 reveal">
          <div className="rounded-lg border border-green-500/20 bg-black/60 overflow-hidden">
            <div className="px-4 py-2 border-b border-green-500/20 bg-green-500/5 flex items-center justify-between">
              <div className="font-mono text-xs text-green-400">EKG MONITOR</div>
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <div className="w-1.5 h-1.5 rounded-full bg-green-400/60 animate-pulse" />
              </div>
            </div>
            <div className="bg-ekg-grid p-6 min-h-[300px] flex items-center justify-center">
              {lesson.rhythm ? (
                <div className="w-full h-[320px]">
                  <EkgStrip rhythm={lesson.rhythm} label={`LEAD II · ${lesson.rhythm.toUpperCase()}`} />
                </div>
              ) : (
                <img
                  src={lesson.image}
                  alt={lesson.title}
                  className="max-h-[360px] w-full object-contain mix-blend-screen opacity-90"
                  data-testid="lesson-ekg-image"
                />
              )}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 reveal" style={{ animationDelay: "80ms" }}>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
            Teaching · {step + 1}
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight mb-4" data-testid="lesson-title">
            {lesson.title}
          </h2>
          <p className="text-slate-300 leading-relaxed">{lesson.body}</p>

          {hasDeep && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowDeep((v) => !v)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md border transition-colors ${
                  showDeep
                    ? "border-green-500/40 bg-green-500/5 text-green-300"
                    : "border-white/15 bg-white/[0.02] text-slate-200 hover:border-green-500/40 hover:bg-green-500/5 hover:text-green-300"
                }`}
                data-testid="lesson-deep-toggle"
                aria-expanded={showDeep}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <BookOpen className="w-4 h-4" />
                  {showDeep ? "Hide deeper dive" : "Learn deeper (optional)"}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showDeep ? "rotate-180" : ""}`}
                />
              </button>

              {showDeep && (
                <div
                  className="mt-4 space-y-4 reveal"
                  data-testid="lesson-deep-content"
                >
                  {lesson.deep.map((para, i) => (
                    <div
                      key={`deep-${step}-${i}`}
                      className="p-4 rounded-md bg-black/40 border border-green-500/15"
                      data-testid={`lesson-deep-para-${i}`}
                    >
                      <div className="font-mono text-[10px] uppercase tracking-widest text-green-400 mb-2">
                        Deeper · {i + 1}
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed">{para}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <Button
          variant="ghost"
          disabled={step === 0}
          onClick={() => setStep(step - 1)}
          className="text-slate-300 hover:text-white hover:bg-white/5"
          data-testid="lesson-prev-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {step < total - 1 ? (
          <Button
            onClick={() => setStep(step + 1)}
            className="rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold px-6"
            data-testid="lesson-next-btn"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={onFinish}
            className="rounded-full bg-red-500 hover:bg-red-400 text-white font-semibold px-6 glow-red"
            data-testid="lesson-to-quiz-btn"
          >
            Take the quiz <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

function QuizView({ level, step, setStep, answers, setAnswers, onSubmit, submitting }) {
  const q = level.quiz[step];
  const total = level.quiz.length;
  const chosen = answers[step];
  const progress = Math.round(((step + 1) / total) * 100);

  const isMcq = q.type !== "click";
  const hasAnswer = isMcq
    ? Number.isInteger(chosen) && chosen >= 0
    : chosen && typeof chosen === "object" && chosen.x !== undefined;

  const pickMcq = (i) => {
    const copy = [...answers];
    copy[step] = i;
    setAnswers(copy);
  };

  const pickClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const copy = [...answers];
    copy[step] = { x, y };
    setAnswers(copy);
  };

  const allAnswered = answers.every(
    (a) =>
      Number.isInteger(a) && a >= 0
        ? true
        : a && typeof a === "object" && a.x !== undefined
  );

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-10">
      <div className="mb-6">
        <div className="flex justify-between text-xs font-mono text-slate-500 mb-2">
          <span>
            Question {step + 1}/{total}
          </span>
          <span>{progress}%</span>
        </div>
        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-red-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="reveal" data-testid={`quiz-question-${step}`}>
        <div className="font-mono text-xs uppercase tracking-widest text-red-400 mb-3">
          {isMcq ? `Trial · ${step + 1}` : `Image ID · ${step + 1}`}
        </div>
        <h2
          className="font-display text-2xl sm:text-3xl font-bold leading-tight mb-8"
          data-testid="quiz-question-text"
        >
          {q.q}
        </h2>

        {isMcq ? (
          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const selected = chosen === i;
              return (
                <button
                  key={`opt-${step}-${i}`}
                  onClick={() => pickMcq(i)}
                  className={`w-full text-left p-4 rounded-md border transition-colors flex items-center gap-4 ${
                    selected
                      ? "border-green-500/60 bg-green-500/10"
                      : "border-white/10 bg-white/[0.02] hover:border-white/30 hover:bg-white/[0.04]"
                  }`}
                  data-testid={`quiz-option-${i}`}
                >
                  <span className="font-mono text-xs w-6 h-6 rounded-full border border-white/20 flex items-center justify-center flex-none">
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="text-slate-200">{opt}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div>
            <div
              className="relative rounded-lg border border-green-500/30 bg-black overflow-hidden cursor-crosshair select-none"
              onClick={pickClick}
              data-testid="quiz-click-image"
            >
              <div className="px-4 py-2 border-b border-green-500/20 bg-green-500/5 flex items-center justify-between">
                <div className="font-mono text-xs text-green-400">EKG · CLICK TO IDENTIFY</div>
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              </div>
              <div className="bg-ekg-grid relative min-h-[320px] flex items-center justify-center">
                {q.rhythm ? (
                  <div className="w-full h-[320px]">
                    <EkgStrip rhythm={q.rhythm} label={`${q.rhythm.toUpperCase()} · CLICK TO ANSWER`} />
                  </div>
                ) : (
                  <img
                    src={q.image}
                    alt="EKG for identification"
                    className="w-full max-h-[420px] object-contain opacity-95 pointer-events-none"
                    draggable={false}
                  />
                )}
                {hasAnswer && (
                  <div
                    className="absolute w-8 h-8 -ml-4 -mt-4 rounded-full border-2 border-green-400 bg-green-400/30 pointer-events-none glow-green"
                    style={{
                      left: `${chosen.x * 100}%`,
                      top: `${chosen.y * 100}%`,
                    }}
                    data-testid="quiz-click-marker"
                  />
                )}
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500 font-mono">
              {hasAnswer
                ? "Marker placed. Adjust by clicking again, or continue."
                : "Click anywhere on the tracing to place your marker."}
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <Button
          variant="ghost"
          disabled={step === 0}
          onClick={() => setStep(step - 1)}
          className="text-slate-300 hover:text-white hover:bg-white/5"
          data-testid="quiz-prev-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {step < total - 1 ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={!hasAnswer}
            className="rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold px-6 disabled:opacity-30"
            data-testid="quiz-next-btn"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            disabled={!hasAnswer || submitting || !allAnswered}
            className="rounded-full bg-red-500 hover:bg-red-400 text-white font-semibold px-8 glow-red disabled:opacity-30"
            data-testid="quiz-submit-btn"
          >
            {submitting ? "Grading…" : "Submit"}
          </Button>
        )}
      </div>
    </div>
  );
}

function ResultView({ level, result, onRetry, onReviewLessons }) {
  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-10 text-center">
      <div className="reveal">
        {result.passed ? (
          <>
            <div className="mx-auto mb-6 pulse-glow inline-block">
              <NinjaBadge level={level.id} size={140} glow />
            </div>
            <div className="font-mono text-xs uppercase tracking-widest text-green-400 mb-2">
              Belt Earned
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-glow-green" data-testid="quiz-result-passed">
              {result.new_rank ? `You are now a ${result.new_rank}` : "Passed!"}
            </h1>
            <p className="text-slate-300 mt-4 max-w-lg mx-auto">
              You scored <span className="text-green-400 font-mono">{result.percent}%</span> ({result.correct}/{result.total}).{" "}
              {result.xp_earned > 0 ? `+${result.xp_earned} XP awarded.` : "No new XP (already completed)."}
            </p>
          </>
        ) : (
          <>
            <div className="w-24 h-24 mx-auto mb-6 clip-hex bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-black" />
            </div>
            <div className="font-mono text-xs uppercase tracking-widest text-red-400 mb-2">
              Not Yet, Ninja
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-glow-red" data-testid="quiz-result-failed">
              Train harder
            </h1>
            <p className="text-slate-300 mt-4 max-w-lg mx-auto">
              You scored <span className="text-red-400 font-mono">{result.percent}%</span> ({result.correct}/{result.total}). You need 80% to earn the belt.
            </p>
          </>
        )}
      </div>

      {/* Review */}
      <div className="mt-10 text-left">
        <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4">
          Sensei's Review · Every Answer Explained
        </div>
        <div className="space-y-3">
          {level.quiz.map((q, i) => {
            const isRight = result.correct_flags[i];
            const r = result.review?.[i] || {};
            return (
              <div
                key={`review-${i}-${q.q?.slice(0, 24) || ""}`}
                className={`p-4 rounded-md border ${
                  isRight ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"
                }`}
                data-testid={`review-item-${i}`}
              >
                <div className="flex items-start gap-3">
                  {isRight ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-none mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400 flex-none mt-0.5" />
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-100">{q.q}</div>
                    {q.type !== "click" && !isRight && r.correct_label && (
                      <div className="text-xs text-slate-400 mt-1">
                        Correct answer:{" "}
                        <span className="text-green-400">{r.correct_label}</span>
                      </div>
                    )}
                    {q.type === "click" && (
                      <div className="text-xs text-slate-400 mt-1">
                        {isRight ? "Great click — you found the target." : "Your click was outside the target region."}
                      </div>
                    )}
                    {r.explanation && (
                      <div
                        className="mt-3 p-3 rounded bg-black/40 border border-white/5 text-sm text-slate-300 leading-relaxed"
                        data-testid={`review-explanation-${i}`}
                      >
                        <span className="font-mono text-[10px] uppercase tracking-widest text-green-400 mr-2">
                          Sensei
                        </span>
                        {r.explanation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        {!result.passed && (
          <Button
            onClick={onRetry}
            className="rounded-full bg-red-500 hover:bg-red-400 text-white font-semibold px-6"
            data-testid="result-retry-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Retry quiz
          </Button>
        )}
        <Button
          onClick={onReviewLessons}
          variant="outline"
          className="rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white"
          data-testid="result-review-lessons-btn"
        >
          Review lessons
        </Button>
        <Link to="/dashboard" data-testid="result-dashboard-link">
          <Button className="rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold px-6">
            Back to Dojo
          </Button>
        </Link>
      </div>
    </div>
  );
}
